/**
 * Auth context and capability helper.
 *
 * Single source of truth for "who is logged in and what can they see".
 * Loads /api/auth/me on mount; components call `useAuth()` to read the
 * current user, `useCan(capability, resource?)` to decide whether to
 * render an affordance. All security decisions are still re-checked by
 * the server; this helper is UX-only.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type Role = 'admin' | 'viewer'

export type User = {
  id: string
  email: string
  display_name: string
  role: Role
  must_change_password: boolean
}

export type Capabilities = {
  global: string[]
  per_site: Record<string, string[]>
  per_camera: Record<string, string[]>
}

export type AuthState = {
  status: 'loading' | 'anon' | 'authed'
  user: User | null
  capabilities: Capabilities
  refresh: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const EMPTY_CAPS: Capabilities = { global: [], per_site: {}, per_camera: {} }

// Exported so tests can supply a hand-built AuthState directly
// (see auth.test.tsx) instead of driving the real /api/auth/me flow.
export const AuthContext = createContext<AuthState | null>(null)

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)cc_csrf=([^;]+)/)
  return m && m[1] ? decodeURIComponent(m[1]) : null
}

export async function fetchJson(path: string, init?: RequestInit): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    ...((init?.headers as Record<string, string>) || {}),
  }
  // Login mints the CSRF cookie, so requests before it have nothing to send.
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const token = readCsrfCookie()
    if (token) headers['X-CSRF-Token'] = token
  }
  return fetch(path, { ...init, headers })
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'anon' | 'authed'>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [capabilities, setCapabilities] = useState<Capabilities>(EMPTY_CAPS)

  const refresh = useCallback(async () => {
    const r = await fetchJson('/api/auth/me')
    if (r.status === 401) {
      setUser(null)
      setCapabilities(EMPTY_CAPS)
      setStatus('anon')
      return
    }
    if (!r.ok) {
      setStatus('anon')
      return
    }
    const body = await r.json()
    setUser(body.user)
    setCapabilities(body.capabilities ?? EMPTY_CAPS)
    setStatus('authed')
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      const r = await fetchJson('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail ?? `Login failed (${r.status})`)
      }
      const body = await r.json()
      setUser(body.user)
      setCapabilities(body.capabilities ?? EMPTY_CAPS)
      setStatus('authed')
    },
    [],
  )

  const logout = useCallback(async () => {
    await fetchJson('/api/auth/logout', { method: 'POST' })
    setUser(null)
    setCapabilities(EMPTY_CAPS)
    setStatus('anon')
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Listen for 401s emitted by the API client so an expired session
  // bounces the user back to /login automatically.
  useEffect(() => {
    function onUnauthorized() {
      setUser(null)
      setCapabilities(EMPTY_CAPS)
      setStatus('anon')
    }
    window.addEventListener('auth:unauthorized', onUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized)
  }, [])

  const value: AuthState = useMemo(
    () => ({ status, user, capabilities, refresh, login, logout }),
    [status, user, capabilities, refresh, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * Client-side capability check. Mirrors server/permissions.py:can().
 * Non-authoritative — every mutation is re-verified on the server.
 */
export function useCan(
  capability: string,
  resource?: { type: 'site' | 'camera'; id: string },
): boolean {
  const { capabilities } = useAuth()
  if (capabilities.global.includes(capability)) return true
  if (!resource) return false
  if (resource.type === 'site') {
    return (capabilities.per_site[resource.id] ?? []).includes(capability)
  }
  return (capabilities.per_camera[resource.id] ?? []).includes(capability)
}
